// src/components/auth/ProtectedRoute.tsx
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { useEffect, useState } from 'react';
import { usePartnerDetails } from '@/hooks/usePartnerDetails';
import { useBranding } from '@/contexts/BrandingContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';

interface UserSettings {
  user_id: string;
  onboarding_completed?: boolean; // Make optional as it might not exist initially
}

export default function ProtectedRoute() {
  const { session, user, loading: authLoading, profile } = useAuth();
  const { data: partnerDetails, isLoading: partnerLoading } = usePartnerDetails();
  const { isLoading: brandingLoading } = useBranding();
  const { isAdmin, isLoading: adminCheckLoading } = useIsAdmin();
  const [brandingInitialized, setBrandingInitialized] = useState(false);
  const location = useLocation();

  // Allow password reset gate without authentication - check this FIRST
  if (location.pathname === '/password-reset/gate') {
    return <Outlet />;
  }

  // Fetch user settings specifically for the onboarding check
  const { data: userSettings, isLoading: settingsLoading, error: settingsError } = useQuery<UserSettings | null>({
    queryKey: ['userSettingsOnboarding', user?.id], // Ensure this key matches invalidation
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('user_settings')
        .select('user_id, onboarding_completed')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching user settings for onboarding:", error);
        throw error;
      }
      // Default to false if no record exists yet for the user
      return data ?? { user_id: user.id, onboarding_completed: false };
    },
    enabled: !!user && !authLoading,
    staleTime: 5 * 60 * 1000, // How long data is considered fresh
    refetchOnWindowFocus: false, // Don't refetch just on window focus
  });

  const shouldShowBrandingLoader = brandingLoading && !brandingInitialized;
  const isLoading = authLoading || settingsLoading || partnerLoading || shouldShowBrandingLoader || adminCheckLoading;

  useEffect(() => {
    if (settingsError) {
        console.error("Failed to load user settings for routing:", settingsError);
        // Potentially show an error state or retry logic here
    }
  }, [settingsError]);

  useEffect(() => {
    if (!brandingLoading && !brandingInitialized) {
      setBrandingInitialized(true);
    }
  }, [brandingLoading, brandingInitialized]);

  // Check if user is pending checkout to prevent navigation conflicts
  const isPendingCheckout = typeof window !== 'undefined' && localStorage.getItem('pendingCheckout') === '1';
  
  // Clear pendingCheckout for external users or if they need onboarding
  useEffect(() => {
    if (isPendingCheckout && user) {
      // Clear for external users
      if (user.user_metadata?.created_via === 'external_checkout_session' ||
          user.user_metadata?.created_via === 'external_one_time_payment') {
        console.log('Clearing pendingCheckout for external user');
        localStorage.removeItem('pendingCheckout');
      }
      // Also clear if user needs onboarding
      if (userSettings && !userSettings.onboarding_completed) {
        console.log('Clearing pendingCheckout for user who needs onboarding');
        localStorage.removeItem('pendingCheckout');
      }
    }
  }, [isPendingCheckout, user, userSettings]);
  
  
  // Never skip navigation logic for users who need onboarding
  if (isPendingCheckout && userSettings && !userSettings.onboarding_completed) {
    // User needs onboarding, ignore pendingCheckout
    console.log('User needs onboarding, ignoring pendingCheckout flag');
  } else if (isPendingCheckout) {
    console.log('ProtectedRoute: User is pending checkout, avoiding redirects');
    return <Outlet />;
  }

  if (isLoading) {
    // Loading state: Show a spinner or skeleton screen
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  if (!session || !user) {
    // Not logged in -> Redirect to login
    console.log("ProtectedRoute: No session/user, redirecting to /login.");
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin access control: Admins should only access admin routes
  if (isAdmin) {
    const adminAllowed = [
      '/admin', // Admin routes
    ];
    const isAllowed = adminAllowed.some((p) => location.pathname.startsWith(p));
    if (!isAllowed) {
      console.log(`ProtectedRoute: Admin trying to access non-admin route, redirecting to admin tutorials.`);
      return <Navigate to="/admin/tutorials" replace />;
    }
    // Admin can proceed to admin routes
    return <Outlet />;
  }

  // Partner access control: Check both profile user_type and partner details
  // Sub-accounts (partner_client) should go to normal app
  const isSubAccount = profile?.user_type === 'partner_client';
  const isActualPartner = partnerDetails !== null && !isSubAccount;
  
  // Partners skip onboarding entirely and go to partner dashboard
  if (isActualPartner) {
    // console.log(`ProtectedRoute: User is a partner, skipping onboarding check.`);
    const partnerAllowed = [
      '/partner', // New partner routes
      '/dashboard/billing',
      '/dashboard/account', 
      '/dashboard/resources',
    ];
    const isAllowed = partnerAllowed.some((p) => location.pathname.startsWith(p));
    if (!isAllowed) {
      console.log(`ProtectedRoute: Partner trying to access non-partner route, redirecting to partner dashboard.`);
      return <Navigate to="/partner/dashboard" replace />;
    }
  } else {
    // Regular users (not partners) must complete onboarding
    if (userSettings && !userSettings.onboarding_completed && location.pathname !== '/onboarding') {
       console.log(`ProtectedRoute: Regular user, onboarding NOT complete (settings found: ${userSettings.onboarding_completed}), path is "${location.pathname}". Redirecting to /onboarding.`);
       return <Navigate to="/onboarding" replace />;
    }
  }
  return <Outlet />;
}